import apiClient, { toApiError } from "../client";
import { Stock } from "@/types/stock.types";
import { ApiResponse } from "@/types/api.types";

export interface StockFilterRequest {
  price?: [number, number];
  currentVolume?: [number, number];
  float?: [number, number];
  changePercent?: [number, number];
  relativeVolume?: [number, number];
  exchange?: string; // comma-separated: 'NASDAQ,NYSE'
  page?: number;
  enableFilters?: boolean;
}

export interface StockHistoryRequest {
  symbol: string;
  interval: "1d" | "1w" | "1mo" | "3mo" | "6mo" | "1y";
}

export interface StockIntradayRequest {
  symbol: string;
  start: string; // ISO timestamp
  end: string; // ISO timestamp
}

export const stocksApi = {
  // Filter/screen stocks by criteria (replaces getTopGainers)
  filterStocks: async (filters?: StockFilterRequest): Promise<Stock[]> => {
    try {
      const response = await apiClient.post<ApiResponse<Stock[]>>(
        "/stocks/filter",
        filters || { enableFilters: false },
      );
      return response.data.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Get single stock by symbol
  getStock: async (symbol: string): Promise<Stock> => {
    try {
      const response = await apiClient.get<ApiResponse<Stock>>(
        `/stocks/get?symbol=${symbol}`,
      );
      return response.data.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Get historical data for charting
  getStockHistory: async (request: StockHistoryRequest): Promise<any> => {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        "/stocks/history",
        request,
      );
      return response.data.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Get intraday data
  getStockIntraday: async (request: StockIntradayRequest): Promise<any> => {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        "/stocks/intraday",
        request,
      );
      return response.data.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Get stock news
  getStockNews: async (
    symbol: string,
    full: boolean = true,
  ): Promise<{
    news: any[];
    topic: string;
  }> => {
    try {
      const response = await apiClient.post<
        ApiResponse<{
          news: any[];
          topic: string;
        }>
      >("/stocks/news", {
        symbol,
        full,
      });
      console.log(response.data.data);
      return response.data.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  // Search stocks by ticker or company name
  searchStocks: async (
    query: string,
    limit: number = 6,
  ): Promise<StockSearchResult[]> => {
    try {
      const response = await apiClient.get<ApiResponse<StockSearchResult[]>>(
        `/stocks/search?query=${encodeURIComponent(query)}&limit=${limit}`,
      );
      return response.data.data;
    } catch (error) {
      throw toApiError(error);
    }
  },
};

export interface StockSearchResult {
  ticker: string;
  companyName: string;
  exchange: string;
  quoteType: string;
  // Yahoo's search endpoint carries no pricing. price/changePercent were
  // declared here but never populated, so anything reading them got undefined.
}
